/**
 * Music mix math — pure, no Web Audio.
 *
 * The music bus plays one loop set: a few bar-aligned loops in one key at one
 * tempo, one source each, on one clock. A "stem" (mood) is a mix preset: a
 * gain per loop. The conductor weights the stems (calm 0.4 + tension 0.6, a
 * combat overlay, …) and this module turns the weights into one gain per
 * loop, normalised so no loop ever exceeds 1 however the weights stack.
 *
 * Every set also has a synthesized `heartbeat` voice (renderHeartbeat) that
 * the low-health preset leans on.
 */

export type MusicStemId = 'calm' | 'tension' | 'combat' | 'lowhealth' | 'endgame';
export const MUSIC_STEM_IDS: MusicStemId[] = ['calm', 'tension', 'combat', 'lowhealth', 'endgame'];

export const HEARTBEAT_LOOP = 'heartbeat';

export type MixPreset = Record<string, number>;

export type MusicSetId = 'composed' | 'original' | 'ground';

export interface MusicSet {
  id: MusicSetId;
  label: string;
  /** Tempo and length — for bar-aligned work; 0 bpm = unknown. */
  bpm: number;
  beatsPerBar: number;
  bars: number;
  /** Loop id → URL. The heartbeat voice is synthesized and implicit. */
  loops: Record<string, string>;
  /** Per mood, a gain per loop id — or per variant family (missing = 0). */
  presets: Record<MusicStemId, MixPreset>;
  /**
   * 67.22: variant families — a family's name (also its first loop) → the loops that take turns in its place. All
   * run in sync; at each loop boundary the game picks one per family at random, so a loop is not heard back to back.
   */
  variants?: Record<string, string[]>;
  /** 67.22: in calm, the chance at a loop boundary that the music drops out for one loop (never two in a row). */
  rests?: { chance: number };
}

/**
 * Composed set (scripts/music/compose.py): six purpose-made layers in
 * D dorian at 84 BPM, 8 bars. Moods are different material here —
 * calm is pad and shimmer, tension adds the pulse, combat is the lead over
 * the full rhythm section.
 */
export const COMPOSED_SET: MusicSet = {
  id: 'composed',
  label: 'COMPOSED',
  bpm: 84,
  beatsPerBar: 4,
  bars: 8,
  loops: {
    pad: 'audio/music/v2/pad.ogg',
    pulse: 'audio/music/v2/pulse.ogg',
    bass: 'audio/music/v2/bass.ogg',
    drums: 'audio/music/v2/drums.ogg',
    lead: 'audio/music/v2/lead.ogg',
    shimmer: 'audio/music/v2/shimmer.ogg',
  },
  presets: {
    calm:      { pad: 1.0, shimmer: 0.55, bass: 0.35 },
    tension:   { pad: 0.7, pulse: 1.0, bass: 0.8, drums: 0.45, shimmer: 0.2 },
    combat:    { pad: 0.45, pulse: 0.7, bass: 1.0, drums: 1.0, lead: 1.0 },
    lowhealth: { pad: 0.3, bass: 0.45, heartbeat: 1.0 },
    endgame:   { pad: 0.8, pulse: 0.9, bass: 1.0, drums: 1.0, lead: 0.9, shimmer: 0.8, heartbeat: 0.15 },
  },
};

/**
 * Original set: one ~30 s track pulled apart by a stem separator. The moods
 * can only be balances of the same material. Kept selectable in Settings.
 */
export const ORIGINAL_SET: MusicSet = {
  id: 'original',
  label: 'ORIGINAL',
  bpm: 0,
  beatsPerBar: 4,
  bars: 0,
  loops: {
    bass: 'audio/music/bass.ogg',
    other: 'audio/music/other.ogg',
    vocals: 'audio/music/vocals.ogg',
    drums: 'audio/music/drums.ogg',
  },
  presets: {
    calm:      { bass: 0.7, other: 0.9, vocals: 0.5 },
    tension:   { bass: 0.9, other: 0.8, vocals: 0.7, drums: 0.6 },
    combat:    { bass: 1.0, other: 0.7, vocals: 0.9, drums: 1.0 },
    lowhealth: { bass: 0.5, drums: 0.4, heartbeat: 1.0 },
    endgame:   { bass: 1.0, other: 1.0, vocals: 1.0, drums: 1.0, heartbeat: 0.3 },
  },
};

/**
 * Ground set — the action clock's (67.19; reworked as 67.22's pick, `compose.py --set ground`). The owner auditioned
 * three directions and chose the sparse one (*"less intrusive … better than base"*): D phrygian at 90 BPM, a low
 * drone with a flat-second rub, metallic ticks with rests, taiko-like toms, a two-note lead. **No shimmer** (*"really
 * annoying and gets into disharmonies"*). **Variants** each loop (a second pulse, a second kit with fills in other bars,
 * a second lead) and **rests** in calm, against *"it'll get a bit old over time"*. **Low health** is the military
 * set's colour in this key (*"military low health is sort of cool"*): its brassy pad and driving eighth bass under the
 * heartbeat. The moods are balanced by the loops' measured levels (set.json `rms`): combat within about 1 dB of calm
 * (*"combat and all that is definitely too loud"*). The L0 fight plays it whenever Settings has the composed music.
 */
export const GROUND_SET: MusicSet = {
  id: 'ground',
  label: 'GROUND',
  bpm: 90,
  beatsPerBar: 4,
  bars: 8,
  loops: {
    pad: 'audio/music/ground/pad.ogg',
    pulse: 'audio/music/ground/pulse.ogg',
    pulse2: 'audio/music/ground/pulse2.ogg',
    bass: 'audio/music/ground/bass.ogg',
    drums: 'audio/music/ground/drums.ogg',
    drums2: 'audio/music/ground/drums2.ogg',
    lead: 'audio/music/ground/lead.ogg',
    lead2: 'audio/music/ground/lead2.ogg',
    lowpad: 'audio/music/ground/lowpad.ogg',
    lowbass: 'audio/music/ground/lowbass.ogg',
  },
  presets: {
    calm:      { pad: 1.0, bass: 0.3 },
    tension:   { pad: 0.8, pulse: 1.0, bass: 0.55, drums: 0.35 },
    combat:    { pad: 0.45, pulse: 0.7, bass: 0.7, drums: 0.8, lead: 0.6 },
    lowhealth: { lowpad: 0.3, lowbass: 0.4, heartbeat: 1.0 },
    endgame:   { pad: 0.6, pulse: 0.7, bass: 0.7, drums: 0.7, lead: 0.7, heartbeat: 0.15 },
  },
  variants: { pulse: ['pulse', 'pulse2'], drums: ['drums', 'drums2'], lead: ['lead', 'lead2'] },
  rests: { chance: 0.3 },
};

export const MUSIC_SETS: Record<MusicSetId, MusicSet> = { composed: COMPOSED_SET, original: ORIGINAL_SET, ground: GROUND_SET };

/** 67.19: the set a match plays — the action clock's own (ground) when the player chose the composed music; the original track everywhere when they chose it. */
export function musicSetFor(chosen: MusicSetId, action: boolean): MusicSetId {
  if (chosen === 'original') return 'original';
  return action ? 'ground' : 'composed';
}
export const DEFAULT_MUSIC_SET: MusicSetId = 'composed';

/** Every voice the set plays: its loops plus the synthesized heartbeat. */
export function loopIdsOf(set: MusicSet): string[] {
  return [...Object.keys(set.loops), HEARTBEAT_LOOP];
}

/** 67.22: which loop plays each variant family this loop, and whether the music is resting (silent) for it. */
export interface LoopPlan { active: Record<string, string>; resting: boolean }

/**
 * Weighted sum of presets per loop, then scaled so the loudest loop is at most 1. 67.22: a preset naming a variant
 * family plays the family's active loop (its first when the plan names none); a resting plan is silence.
 */
export function mixLoopGains(weights: Partial<Record<MusicStemId, number>>, set: MusicSet, plan?: LoopPlan): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of loopIdsOf(set)) out[id] = 0;
  if (plan?.resting) return out;
  for (const stem of MUSIC_STEM_IDS) {
    const w = weights[stem] ?? 0;
    if (w <= 0) continue;
    const preset = set.presets[stem];
    for (const key of Object.keys(preset)) {
      const family = set.variants?.[key];
      const id = family ? (plan?.active[key] ?? family[0]) : key;
      if (!(id in out)) continue; // a preset naming a loop the set lacks is ignored
      out[id] += w * preset[key];
    }
  }
  let peak = 0;
  for (const id of Object.keys(out)) if (out[id] > peak) peak = out[id];
  if (peak > 1) for (const id of Object.keys(out)) out[id] /= peak;
  return out;
}

/**
 * 67.22: the plan for the next loop. Each variant family moves to another of its loops more often than not (so the
 * same pass is rarely heard twice running); in calm alone — no tension, combat, low health or endgame in the mix —
 * the music may rest for a loop at the set's chance, never two in a row. `rnd` is the caller's (presentation only).
 */
export function nextLoopPlan(set: MusicSet, weights: Partial<Record<MusicStemId, number>>, prev: LoopPlan, rnd: () => number): LoopPlan {
  const active: Record<string, string> = { ...prev.active };
  for (const [family, ids] of Object.entries(set.variants ?? {})) {
    if (ids.length < 2 || rnd() >= 0.6) continue;
    const others = ids.filter(id => id !== (prev.active[family] ?? ids[0]));
    active[family] = others[Math.min(others.length - 1, Math.floor(rnd() * others.length))];
  }
  const busy = (weights.tension ?? 0) + (weights.combat ?? 0) + (weights.lowhealth ?? 0) + (weights.endgame ?? 0);
  const calmOnly = (weights.calm ?? 0) >= 0.5 && busy < 0.1;
  const resting = !!set.rests && calmOnly && !prev.resting && rnd() < set.rests.chance;
  return { active, resting };
}

/** 67.22: does a mix at these weights still allow a rest (calm alone)? A rest under way ends the moment it does not. */
export function restAllowed(weights: Partial<Record<MusicStemId, number>>): boolean {
  const busy = (weights.tension ?? 0) + (weights.combat ?? 0) + (weights.lowhealth ?? 0) + (weights.endgame ?? 0);
  return busy < 0.1;
}

/** Seconds per bar for bar-aligned work; null when the set's tempo is unknown. */
export function barSeconds(set: MusicSet): number | null {
  return set.bpm > 0 ? (60 / set.bpm) * set.beatsPerBar : null;
}

/**
 * Heartbeat loop: a lub-dub at `bpm` rendered into a mono buffer of `beats`
 * beats. Pure sample generation; the caller wraps it in an AudioBuffer.
 */
export function renderHeartbeat(sampleRate: number, bpm = 72, beats = 4): Float32Array {
  const beatLen = Math.round((60 / bpm) * sampleRate);
  const data = new Float32Array(beatLen * beats);
  const thump = (start: number, freq: number, amp: number, decay: number) => {
    const n = Math.round(decay * sampleRate);
    for (let i = 0; i < n && start + i < data.length; i++) {
      const t = i / sampleRate;
      const env = Math.exp(-t / (decay * 0.3)) * (1 - Math.exp(-t * 400));
      data[start + i] += Math.sin(2 * Math.PI * (freq * (1 + 0.6 * Math.exp(-t * 60))) * t) * amp * env;
    }
  };
  for (let b = 0; b < beats; b++) {
    const s = b * beatLen;
    thump(s, 52, 0.9, 0.22);                          // lub
    thump(s + Math.round(beatLen * 0.3), 44, 0.6, 0.18); // dub
  }
  return data;
}

/** The ramp a stem takes when its weight moves (seconds). Slow to return to calm, fast into combat. */
export function stemRampSeconds(stem: MusicStemId, prev: number, next: number): number {
  const increasing = next > prev;
  switch (stem) {
    case 'calm':      return increasing ? 3.0 : 1.5;
    case 'tension':   return increasing ? 1.5 : 0.8;
    case 'combat':    return increasing ? 0.8 : 3.0;
    case 'lowhealth': return 1.0;
    case 'endgame':   return 1.0;
  }
}

/**
 * Match phase for the music. 'endgame' means the match is nearly decided:
 * with three or more teams, two left; with two teams, the enemy down to its
 * last unit after having more. A 1v1 is never "endgame" from turn one.
 */
export function deriveMatchPhase(
  aliveTeams: number,
  totalTeams: number,
  aliveEnemyUnits: number,
  startingEnemyUnits: number,
): 'early' | 'mid' | 'endgame' {
  if (totalTeams > 2) {
    if (aliveTeams <= 2) return 'endgame';
    if (aliveTeams <= totalTeams * 0.5) return 'mid';
    return 'early';
  }
  if (startingEnemyUnits > 1 && aliveEnemyUnits <= 1) return 'endgame';
  if (startingEnemyUnits > 1 && aliveEnemyUnits <= startingEnemyUnits * 0.5) return 'mid';
  return 'early';
}
